import React, { useEffect, useRef } from "react";
import {
  FaCalendarAlt,
  FaCheck,
  FaCopy,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaTimes,
} from "react-icons/fa";

import {
  DEFAULT_EVENT_IMAGE,
  formatEventDate,
  getEventCategories,
  getEventDepartment,
} from "../utils/sharedEventsUtils";

function DetailBlock({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-700 shadow-sm dark:bg-white/10 dark:text-sky-200">
            <Icon />
          </div>
        )}

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-sm font-bold leading-6 text-slate-800 dark:text-white">
            {value || "Non spécifié"}
          </p>
        </div>
      </div>
    </div>
  );
}

function SharedEventDetailsModal({
  isOpen,
  event,
  onClose,
  onCopy,
  isCopying = false,
  isCopied = false,
}) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !event) return null;

  const categories = getEventCategories(event);
  const department = getEventDepartment(event);

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/70 px-3 backdrop-blur-sm sm:items-center sm:px-4">
      <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[2rem] border border-white/20 bg-white shadow-2xl dark:bg-slate-900 sm:rounded-[2rem]">
        <div className="relative min-h-52 overflow-hidden bg-slate-950 sm:min-h-72">
          <img
            src={event?.photo_url || DEFAULT_EVENT_IMAGE}
            alt={event?.titre || "Événement partagé"}
            className="absolute inset-0 h-full w-full object-cover opacity-80"
            onError={(eventImage) => {
              eventImage.currentTarget.src = DEFAULT_EVENT_IMAGE;
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />

          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25 focus:outline-none focus:ring-4 focus:ring-white/30"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>

          <div className="absolute bottom-5 left-5 right-5">
            <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold text-white backdrop-blur-xl">
              <FaInfoCircle />
              Détail de l'événement partagé
            </p>

            <h2 className="mt-3 line-clamp-2 font-titre text-3xl font-bold text-white sm:text-4xl">
              {event?.titre || "Événement sans titre"}
            </h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/[0.05]">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Description
              </p>
              <p className="mt-3 whitespace-pre-line text-sm font-medium leading-7 text-slate-600 dark:text-white/65">
                {event?.description || "Aucune description disponible."}
              </p>
            </div>

            <div className="grid gap-3">
              <DetailBlock
                icon={FaCalendarAlt}
                label="Date"
                value={formatEventDate(event?.start)}
              />

              <DetailBlock
                icon={FaMapMarkerAlt}
                label="Lieu"
                value={event?.lieu_event}
              />
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <DetailBlock label="Horaires" value={event?.horaire_event} />
            <DetailBlock label="Type" value={event?.type_event} />
            <DetailBlock label="Niveau" value={event?.level_event} />
            <DetailBlock
              label="Département"
              value={department ? `Département ${department}` : "Non spécifié"}
            />
          </div>

          <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-white/[0.03]">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
              Catégories concernées
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {categories.length > 0 ? (
                categories.map((category) => (
                  <span
                    key={category}
                    className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-white/70"
                  >
                    {category}
                  </span>
                ))
              ) : (
                <span className="text-sm font-medium text-slate-500 dark:text-white/50">
                  Aucune catégorie renseignée.
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 grid gap-2 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:grid-cols-2">
          <button
            type="button"
            onClick={onCopy}
            disabled={isCopying}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-white/10 dark:disabled:text-white/40"
          >
            {isCopied ? <FaCheck /> : <FaCopy />}
            {isCopying
              ? "Copie en cours..."
              : isCopied
                ? "Déjà copié"
                : "Copier dans mon agenda"}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export default SharedEventDetailsModal;